/**
 * Audit-Log view model — pure derivations over `AuditLogResponse`.
 *
 * The server reconstructs the chronicle; this module only decides how each
 * step reads: a headline, the conclusion it stands for ("Schluss"), a tone,
 * the filter group and the day it belongs to. Nothing here does I/O.
 *
 * Texts are translated at call time through the global i18n instance; the
 * components rendering them call `useLingui()` so they follow a language switch.
 */

import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg, plural, t } from '@lingui/core/macro';

import { intlLocale, translatedRecord } from '../../lib/locale';
import type {
  AdvisoryAuditStep, AssessmentStatus, AuditCase, AuditEntry, AuditKind, AuditVerdict, Bewertung,
  DeadlineEventType,
} from '../../lib/types';
import { BEWERTUNG_LABELS, STATUS_LABELS } from '../triage/eventHelpers';

export type AuditTone = 'neutral' | 'good' | 'warn' | 'alert' | 'info';

/** Filter groups offered above the timeline. */
export type AuditGroup = 'alle' | 'bewertungen' | 'behoerde' | 'meldungen' | 'nachweise' | 'system';

/** Labels are translated at render with `i18n._()`. */
export const AUDIT_GROUPS: readonly { key: AuditGroup; label: MessageDescriptor }[] = [
  { key: 'alle', label: msg`Alle` },
  { key: 'bewertungen', label: msg`Bewertungen` },
  { key: 'behoerde', label: msg`Behördenmeldung` },
  { key: 'meldungen', label: msg`Kundenmeldungen` },
  { key: 'nachweise', label: msg`Nachweise` },
  { key: 'system', label: msg`System` },
];

const GROUP_OF: Readonly<Record<AuditKind, Exclude<AuditGroup, 'alle'>>> = {
  bewertung: 'bewertungen',
  pruefung: 'bewertungen',
  produktbewertung: 'bewertungen',
  instanzbewertung: 'bewertungen',
  meldung: 'meldungen',
  postausgang: 'meldungen',
  versand: 'meldungen',
  advisory: 'nachweise',
  nachweis: 'nachweise',
  erkannt: 'system',
  behoben: 'system',
  wiedereroeffnet: 'system',
  fall: 'behoerde',
  kernangaben: 'behoerde',
  behoerdenmeldung: 'behoerde',
  abhilfe: 'behoerde',
  fristereignis: 'behoerde',
};

export const KIND_LABELS: Readonly<Record<AuditKind, string>> = translatedRecord<AuditKind>({
  erkannt: msg`Erkennung`,
  bewertung: msg`Komponentenbewertung`,
  pruefung: msg`In Prüfung`,
  produktbewertung: msg`Produktbewertung`,
  instanzbewertung: msg`Instanzbewertung`,
  wiedereroeffnet: msg`Wiedereröffnung`,
  behoben: msg`Automatischer Abschluss`,
  meldung: msg`Kundenmeldung`,
  postausgang: msg`Postausgang`,
  versand: msg`Versand`,
  advisory: msg`Advisory`,
  nachweis: msg`Nachweis`,
  fall: msg`Schwachstellenfall`,
  kernangaben: msg`Kernangaben`,
  behoerdenmeldung: msg`Behördenmeldung`,
  abhilfe: msg`Abhilfemaßnahme`,
  fristereignis: msg`Fristereignis`,
});

export function groupOf(entry: AuditEntry): Exclude<AuditGroup, 'alle'> {
  return GROUP_OF[entry.kind];
}

const MANUAL: ReadonlySet<AuditVerdict> = new Set(['BETROFFEN', 'NICHT_BETROFFEN']);

/** A decided verdict replaced by a different one. */
export function isReassessment(entry: AuditEntry): boolean {
  return (
    entry.kind === 'bewertung' &&
    entry.vorher !== null &&
    MANUAL.has(entry.vorher) &&
    entry.vorher !== entry.bewertung
  );
}

function isStatus(value: AuditVerdict): value is AssessmentStatus {
  return Object.hasOwn(STATUS_LABELS, value);
}

/** Steps of the Security Advisory (DEV-129). */
const ADVISORY_STEP_LABELS: Readonly<Record<AdvisoryAuditStep, string>> = translatedRecord<AdvisoryAuditStep>({
  SAVED: msg`Gespeichert`,
  RELEASED: msg`Freigegeben`,
  WITHDRAWN: msg`Freigabe zurückgenommen`,
});

function isAdvisoryStep(value: AuditVerdict): value is AdvisoryAuditStep {
  return Object.hasOwn(ADVISORY_STEP_LABELS, value);
}

/** Deadline events of a vulnerability case (DEV-132). */
const DEADLINE_EVENT_LABELS: Readonly<Record<DeadlineEventType, string>> = translatedRecord<DeadlineEventType>({
  STARTED: msg`Frist läuft`,
  DUE_SOON: msg`Frist läuft bald ab`,
  OVERDUE: msg`Frist überschritten`,
});

function isDeadlineEvent(value: AuditVerdict): value is DeadlineEventType {
  return Object.hasOwn(DEADLINE_EVENT_LABELS, value);
}

const DEADLINE_EVENT_TONE: Readonly<Record<DeadlineEventType, AuditTone>> = {
  STARTED: 'info',
  DUE_SOON: 'warn',
  OVERDUE: 'alert',
};

function deadlineEventTone(entry: AuditEntry): AuditTone {
  return entry.bewertung !== null && isDeadlineEvent(entry.bewertung) ? DEADLINE_EVENT_TONE[entry.bewertung] : 'info';
}

/** A report recorded late, or taken back, needs a second look. `detail` is the server's (German) text. */
function reportTone(entry: AuditEntry): AuditTone {
  return entry.fristgerecht === false || entry.detail?.includes('zurückgenommen') ? 'warn' : 'good';
}

/** Case entries carry "<what> · <CVE> · <product>"; the headline reads "<what>: <CVE> · <product>". */
function caseTitle(entry: AuditEntry, fallback: string): string {
  const [what, ...subject] = (entry.detail ?? '').split(' · ');
  if (!what) return fallback;
  return subject.length > 0 ? `${what}: ${subject.join(' · ')}` : what;
}

function advisoryTitle(entry: AuditEntry, cve: string): string {
  if (entry.bewertung === 'RELEASED') return t`Advisory zu ${cve} freigegeben`;
  if (entry.bewertung === 'WITHDRAWN') return t`Freigabe des Advisorys zu ${cve} zurückgenommen`;
  return t`Advisory zu ${cve} gespeichert`;
}

export function verdictLabel(value: AuditVerdict | null): string {
  if (value === null) return t`Offen`;
  if (isAdvisoryStep(value)) return ADVISORY_STEP_LABELS[value];
  if (isDeadlineEvent(value)) return DEADLINE_EVENT_LABELS[value];
  return isStatus(value) ? STATUS_LABELS[value] : BEWERTUNG_LABELS[value as Bewertung];
}

/** The product or instance a level entry refers to: the first part of its detail. */
function levelSubject(entry: AuditEntry): string {
  return entry.detail?.split(' · ')[0] ?? '';
}

export function toneOf(entry: AuditEntry): AuditTone {
  switch (entry.kind) {
    case 'bewertung':
      return entry.bewertung === 'BETROFFEN' ? 'alert' : 'good';
    case 'produktbewertung':
    case 'instanzbewertung':
      return entry.bewertung === 'AFFECTED' ? 'alert'
        : entry.bewertung === 'NOT_AFFECTED' || entry.bewertung === 'FIXED' ? 'good' : 'info';
    case 'pruefung':
      return 'info';
    case 'behoben':
      return 'good';
    case 'wiedereroeffnet':
      return 'warn';
    case 'versand':
      return 'good';
    case 'meldung':
    case 'fall':
      return 'info';
    case 'behoerdenmeldung':
      return reportTone(entry);
    case 'fristereignis':
      return deadlineEventTone(entry);
    case 'postausgang':
      return entry.detail === 'Versand fehlgeschlagen' ? 'warn' : 'info';
    default:
      return 'neutral';
  }
}

function komponenteLabel(entry: AuditEntry | AuditCase): string {
  const { name, version } = entry.komponente;
  if (!name) return t`Komponente`;
  return version ? `${name} ${version}` : name;
}

export { komponenteLabel };

/** Headline of one step, sentence case, in the UI language. */
export function titleOf(entry: AuditEntry): string {
  const cve = entry.cve_id ?? 'CVE';
  switch (entry.kind) {
    case 'erkannt': {
      const component = komponenteLabel(entry);
      return t`KEV-Treffer ${cve} in ${component} erkannt`;
    }
    case 'bewertung': {
      if (isReassessment(entry)) {
        const verdict = verdictLabel(entry.bewertung).toLowerCase();
        return t`${cve} neu bewertet: ${verdict}`;
      }
      if (entry.vorher === entry.bewertung) return t`Bewertung von ${cve} bestätigt`;
      return entry.bewertung === 'BETROFFEN'
        ? t`${cve} als betroffen bewertet`
        : t`${cve} als nicht betroffen bewertet`;
    }
    case 'pruefung':
      return t`Prüfung von ${cve} begonnen`;
    case 'produktbewertung': {
      const subject = levelSubject(entry) || t`Produktversion`;
      const verdict = verdictLabel(entry.bewertung).toLowerCase();
      return t`${subject}: ${cve} ${verdict}`;
    }
    case 'instanzbewertung': {
      const subject = levelSubject(entry) || t`Instanz`;
      if (entry.bewertung === 'OPEN') return t`${subject}: Abweichung zu ${cve} entfernt`;
      const verdict = verdictLabel(entry.bewertung).toLowerCase();
      return t`${subject}: ${cve} ${verdict}`;
    }
    case 'wiedereroeffnet':
      return t`${cve} automatisch wieder geöffnet`;
    case 'behoben':
      return t`${cve} automatisch als behoben geschlossen`;
    case 'meldung': {
      const count = entry.kunden.length;
      const customer = entry.kunden[0] ?? '';
      return count === 1
        ? t`Kundenmeldung für ${customer} angelegt`
        : t`${count} Kundenmeldungen angelegt`;
    }
    case 'postausgang': {
      // `detail` is the server's outbox state (e.g. „Versand fehlgeschlagen“).
      const detail = entry.detail ?? '';
      const customers = entry.kunden.join(', ');
      const title = entry.kunden.length > 0 ? t`Kundenmeldung für ${customers} ${detail}` : t`Kundenmeldung ${detail}`;
      return title.trim();
    }
    case 'versand': {
      const customers = entry.kunden.join(', ');
      return entry.kunden.length > 0
        ? t`${customers} benachrichtigt`
        : t`Kundenbenachrichtigung versendet`;
    }
    case 'advisory':
      return advisoryTitle(entry, cve);
    case 'nachweis':
      return t`Nachweis zu ${cve} hochgeladen`;
    case 'kernangaben':
      return t`Kernangaben zu ${cve} gespeichert`;
    case 'fall':
    case 'behoerdenmeldung':
    case 'abhilfe':
    case 'fristereignis':
      return caseTitle(entry, t`Schwachstellenfall ${cve}`);
  }
}

const INSTANCE_CONCLUSIONS: Readonly<Record<string, MessageDescriptor>> = {
  NOT_AFFECTED: msg`Die Instanz ist in ihrer Einsatzumgebung nicht betroffen; der Kunde wird dennoch informiert.`,
  OPEN: msg`Die Instanz folgt wieder der Bewertung ihrer Produktversion.`,
};

/** The conclusion a step stands for — null when it records no conclusion. */
export function conclusionOf(entry: AuditEntry): string | null {
  switch (entry.kind) {
    case 'bewertung':
      return entry.bewertung === 'BETROFFEN'
        ? t`Anwendbar auf die Komponentenversion. Vorschlag für die Produktversionen, die sie enthalten.`
        : t`Nicht anwendbar auf die Komponentenversion. Vorschlag für die Produktversionen, die sie enthalten.`;
    case 'produktbewertung':
      return entry.bewertung === 'AFFECTED'
        ? t`Die Produktversion ist betroffen. Die Kunden mit dieser Version werden benachrichtigt.`
        : entry.bewertung === 'NOT_AFFECTED'
          ? t`Die Produktversion ist nicht betroffen.`
          : entry.bewertung === 'FIXED'
            ? t`In dieser Produktversion behoben.`
            : null;
    case 'instanzbewertung': {
      const conclusion = INSTANCE_CONCLUSIONS[entry.bewertung ?? ''];
      return conclusion ? i18n._(conclusion) : null;
    }
    case 'behoben':
      return t`Keine ausgelieferte Konfiguration ist mehr betroffen. Der Fall ist geschlossen.`;
    case 'wiedereroeffnet':
      return t`Der CVE wird erneut gemeldet. Die Anwendbarkeit ist neu zu bewerten.`;
    default:
      return null;
  }
}

export function matchesAuditSearch(entry: AuditEntry, needle: string): boolean {
  const query = needle.trim().toLowerCase();
  if (query === '') return true;
  return [
    entry.cve_id,
    entry.komponente.name,
    entry.komponente.hersteller,
    entry.actor,
    entry.begruendung,
    entry.detail,
    ...entry.kunden,
  ].some((value) => value !== null && value !== undefined && value.toLowerCase().includes(query));
}

export function matchesCaseSearch(entry: AuditCase, needle: string): boolean {
  const query = needle.trim().toLowerCase();
  if (query === '') return true;
  return [
    entry.cve_id,
    entry.komponente.name,
    entry.komponente.hersteller,
    entry.bewertet_von,
    entry.begruendung,
  ].some((value) => value !== null && value.toLowerCase().includes(query));
}

/* --- Dates, durations, people ------------------------------------------- */

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Calendar day of a timestamp, or null without a usable one. */
function dayOf(iso: string | null): Date | null {
  if (iso === null) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function dayLabel(iso: string | null, now = new Date()): string {
  const date = dayOf(iso);
  if (date === null) return t`Ohne Zeitstempel`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (dayKey(date) === dayKey(now)) return t`Heute`;
  if (dayKey(date) === dayKey(yesterday)) return t`Gestern`;
  return date.toLocaleDateString(intlLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function timeLabel(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' });
}

/** Entries grouped per calendar day, keeping the newest-first order. */
export function groupByDay(
  entries: readonly AuditEntry[],
): { key: string; label: string; entries: AuditEntry[] }[] {
  // Grouped by the calendar day, not by its (translated) label.
  const groups: { key: string; day: string; label: string; entries: AuditEntry[] }[] = [];
  for (const entry of entries) {
    const date = dayOf(entry.ts);
    const day = date === null ? 'none' : dayKey(date);
    const last = groups[groups.length - 1];
    if (last?.day === day) last.entries.push(entry);
    else groups.push({ key: `${day}-${groups.length}`, day, label: dayLabel(entry.ts), entries: [entry] });
  }
  return groups.map(({ key, label, entries: own }) => ({ key, label, entries: own }));
}

/** '< 1 h', '4 h', '2,5 Tage' — in the UI language. */
export function fmtDuration(hours: number | null): string {
  if (hours === null) return '—';
  if (hours < 1) return '< 1 h';
  if (hours < 48) return `${Math.round(hours)} h`;
  const days = Math.round((hours / 24) * 10) / 10;
  return t`${plural(days, { one: '# Tag', other: '# Tage' })}`;
}

export function initials(name: string): string {
  const parts = name
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/[\s-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
  return `${first}${last}`.toUpperCase();
}

export function percent(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part / whole) * 100) : null;
}

/* --- Case file stepper -------------------------------------------------- */

export type StepState = 'done' | 'current' | 'warn' | 'skipped' | 'pending';

export interface CaseStep {
  label: string;
  state: StepState;
  hint: string;
}

function reportStep({ gemeldet, ueberfaellig }: AuditCase['fristen']): CaseStep {
  const label = t`Gemeldet`;
  if (ueberfaellig > 0) {
    return { label, state: 'warn', hint: t`${plural(ueberfaellig, { one: '# Frist überschritten', other: '# Fristen überschritten' })}` };
  }
  if (gemeldet > 0) {
    return { label, state: 'done', hint: t`${plural(gemeldet, { one: '# Meldung erfasst', other: '# Meldungen erfasst' })}` };
  }
  return { label, state: 'current', hint: t`Meldung ausstehend` };
}

function informedStep({ gesamt, versendet }: AuditCase['meldungen']): CaseStep {
  const label = t`Informiert`;
  if (gesamt === 0) return { label, state: 'pending', hint: t`ausstehend` };
  return { label, state: versendet >= gesamt ? 'done' : 'current', hint: t`${versendet} / ${gesamt} versendet` };
}

export function caseSteps(entry: AuditCase): CaseStep[] {
  const decided = entry.bewertung === 'BETROFFEN' || entry.bewertung === 'NICHT_BETROFFEN';
  const assessed = t`Bewertet`;
  const steps: CaseStep[] = [
    { label: t`Erkannt`, state: 'done', hint: 'KEV-Scan' },
  ];

  if (entry.bewertung === 'OFFEN') {
    steps.push({ label: assessed, state: 'current', hint: t`Entscheidung fällig` });
  } else if (entry.bewertung === 'BEHOBEN' && entry.entscheidungen === 0) {
    steps.push({ label: assessed, state: 'skipped', hint: t`nicht nötig` });
  } else {
    steps.push({
      label: assessed,
      state: 'done',
      hint: entry.letzter_bewerter ?? entry.bewertet_von ?? t`erfasst`,
    });
  }

  if (entry.bewertung === 'BETROFFEN') {
    // DEV-132: the authority report has the deadlines; customers are informed without one.
    steps.push(reportStep(entry.fristen), informedStep(entry.meldungen));
  } else {
    const closed = decided || entry.bewertung === 'BEHOBEN';
    steps.push({ label: t`Gemeldet`, state: closed ? 'skipped' : 'pending', hint: closed ? t`entfällt` : '—' });
    let closedHint = '—';
    if (entry.bewertung === 'BEHOBEN') closedHint = t`automatisch`;
    else if (closed) closedHint = t`ohne Meldepflicht`;
    steps.push({ label: t`Abgeschlossen`, state: closed ? 'done' : 'pending', hint: closedHint });
  }
  return steps;
}

/* --- Export ------------------------------------------------------------- */

function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Semicolon CSV with BOM, so German Excel opens umlauts and columns correctly.
 * The header and the yes/no values are part of the export format and stay
 * German; labels from the shared helpers follow the UI language.
 */
export function toCsv(entries: readonly AuditEntry[]): string {
  const header = [
    'Zeitpunkt (UTC)',
    'Art',
    'Akteur',
    'CVE',
    'Komponente',
    'Vorher',
    'Bewertung',
    'Schluss',
    'Begründung',
    'Kunden',
    'Detail',
    'Frist 24 h',
    'Fristgerecht',
  ];
  const rows = entries.map((entry) => [
    entry.ts,
    KIND_LABELS[entry.kind],
    entry.actor,
    entry.cve_id,
    komponenteLabel(entry),
    entry.kind === 'bewertung' ? verdictLabel(entry.vorher) : '',
    entry.bewertung === null ? '' : verdictLabel(entry.bewertung),
    conclusionOf(entry),
    entry.begruendung,
    entry.kunden.join(', '),
    entry.detail,
    entry.frist,
    entry.fristgerecht === null ? '' : entry.fristgerecht ? 'ja' : 'nein',
  ]);
  return `﻿${[header, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n')}\r\n`;
}
